import { useState, useEffect } from 'react'
import { Gamepad2, Sparkles, Sliders, ExternalLink } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { cn } from '@/lib/utils'

export interface CardFormData {
  cr_name: string
  name: string
  description: string
  count: number | string
  arena: number | string
  rarity: string
  card_type: string
  transport: string
  target_air: boolean
  target_ground: boolean
  focus: string
  range_tiles: string
  speed: string
  hit_speed: string
  first_hit_speed: string
  cost: number | string
  form_size: string
  concept_image?: File | null
  design_image?: File | null
  cr_image?: File | null
  wiki_link?: string
}

export interface CardOptions {
  rarities: string[]
  card_types: string[]
  transports: string[]
  speeds: string[]
  focuses: string[]
  form_sizes: string[]
}

type Errors = Partial<Record<keyof CardFormData, string>>

interface CardFormProps {
  data: CardFormData
  errors: Errors
  setData: (key: keyof CardFormData, value: string | number | boolean | File | null) => void
  options: CardOptions
  conceptImageUrl?: string | null
  designImageUrl?: string | null
  crImageUrl?: string | null
  disabled?: boolean
  metadataDisabled?: boolean
  crImageDisabled?: boolean
  artImageDisabled?: boolean
}

function Field({
  label,
  htmlFor,
  error,
  children,
  className,
}: {
  label: React.ReactNode
  htmlFor?: string
  error?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-err">{error}</p>}
    </div>
  )
}

const selectClass =
  'flex h-9 w-full items-center justify-between rounded-md border border-border bg-bg px-3 py-1 text-sm focus:outline-none focus:border-accent'

function NativeSelect({
  id,
  value,
  onChange,
  options,
  includeBlank,
  disabled,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  options: string[]
  includeBlank?: boolean
  disabled?: boolean
}) {
  return (
    <select id={id} className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {includeBlank && <option value="">—</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
}

export default function CardForm({
  data,
  errors,
  setData,
  options,
  conceptImageUrl,
  designImageUrl,
  crImageUrl,
  disabled = false,
  metadataDisabled = disabled,
  crImageDisabled = disabled,
  artImageDisabled = disabled,
}: CardFormProps) {
  const [conceptLocalUrl, setConceptLocalUrl] = useState<string | null>(null)
  const [designLocalUrl, setDesignLocalUrl] = useState<string | null>(null)
  const [crLocalUrl, setCrLocalUrl] = useState<string | null>(null)

  useEffect(() => {
    if (data.concept_image) {
      const url = URL.createObjectURL(data.concept_image)
      setConceptLocalUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setConceptLocalUrl(null)
    }
  }, [data.concept_image])

  useEffect(() => {
    if (data.design_image) {
      const url = URL.createObjectURL(data.design_image)
      setDesignLocalUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setDesignLocalUrl(null)
    }
  }, [data.design_image])

  useEffect(() => {
    if (data.cr_image) {
      const url = URL.createObjectURL(data.cr_image)
      setCrLocalUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setCrLocalUrl(null)
    }
  }, [data.cr_image])

  const conceptPreview = conceptLocalUrl || conceptImageUrl
  const designPreview = designLocalUrl || designImageUrl
  const crPreview = crLocalUrl || crImageUrl

  return (
    <div className="space-y-6">
      {/* SECTION 1: Clash Royale Info */}
      <div className="rounded-xl border border-border bg-bg-elev p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border/60">
          <div className="p-2 rounded-lg bg-fg/10 text-fg">
            <Gamepad2 size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-fg">Clash Royale Info</h3>
            <p className="text-sm text-fg-muted">Official card details and source artwork from Clash Royale.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2 space-y-4">
            <Field label="CR Name" htmlFor="cr_name" error={errors.cr_name}>
              <Input
                id="cr_name"
                value={data.cr_name}
                onChange={(e) => setData('cr_name', e.target.value)}
                required
                placeholder="e.g. Knight"
                disabled={metadataDisabled}
              />
            </Field>

            <Field
              label={
                <div className="flex items-center gap-1.5">
                  <span>Wiki Link</span>
                  {data.wiki_link && (
                    <a
                      href={data.wiki_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-fg hover:text-fg/80 transition-colors inline-flex items-center"
                      title="Open wiki link in new tab"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              }
              htmlFor="wiki_link"
              error={errors.wiki_link}
            >
              <div className="relative">
                <Input
                  id="wiki_link"
                  value={data.wiki_link || ''}
                  onChange={(e) => setData('wiki_link', e.target.value)}
                  placeholder="https://clashroyale.fandom.com/wiki/..."
                  className="pr-10"
                  disabled={metadataDisabled}
                />
                {data.wiki_link && (
                  <a
                    href={data.wiki_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg transition-colors"
                    title="Open wiki link in new tab"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </Field>
          </div>
          <div>
            <Field label="CR Card Image" error={errors.cr_image}>
              <div className="flex flex-col gap-3">
                {crPreview ? (
                  <div className="relative group w-32 h-32 rounded-lg overflow-hidden border border-border bg-bg-subtle/30 shadow-sm transition hover:shadow-md">
                    <img
                      src={crPreview}
                      alt="CR Preview"
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-fg-muted bg-bg-subtle/10 text-xs">
                    No image uploaded
                  </div>
                )}
                {!crImageDisabled && (
                  <Input
                    id="cr_image"
                    type="file"
                    accept="image/*"
                    className="cursor-pointer file:text-fg file:font-semibold text-xs"
                    onChange={(e) => setData('cr_image', e.target.files?.[0] || null)}
                  />
                )}
              </div>
            </Field>
          </div>
        </div>
      </div>

      {/* SECTION 2: Roundhouse Info */}
      <div className="rounded-xl border border-border bg-bg-elev p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border/60">
          <div className="p-2 rounded-lg bg-fg/10 text-fg">
            <Sparkles size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-fg">Roundhouse Info</h3>
            <p className="text-sm text-fg-muted">Internal naming, description, concept art, and design files.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-1">
            <Field label="Roundhouse Name" htmlFor="name" error={errors.name}>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                placeholder="e.g. Bouncer"
                disabled={metadataDisabled}
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Description" htmlFor="description" error={errors.description}>
              <Textarea
                id="description"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
                rows={2}
                placeholder="Brief summary of custom gameplay mechanics, role, or design goals..."
                disabled={metadataDisabled}
              />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border/60">
          <Field label="Concept Image" error={errors.concept_image}>
            <div className="flex flex-col gap-3">
              {conceptPreview ? (
                <div className="relative group w-32 h-32 rounded-lg overflow-hidden border border-border bg-bg-subtle/30 shadow-sm transition hover:shadow-md">
                  <img
                    src={conceptPreview}
                    alt="Concept Preview"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-fg-muted bg-bg-subtle/10 text-xs">
                  No image uploaded
                </div>
              )}
              {!artImageDisabled && (
                <Input
                  id="concept_image"
                  type="file"
                  accept="image/*"
                  className="cursor-pointer file:text-fg file:font-semibold text-xs"
                  onChange={(e) => setData('concept_image', e.target.files?.[0] || null)}
                />
              )}
            </div>
          </Field>

          <Field label="Design Image" error={errors.design_image}>
            <div className="flex flex-col gap-3">
              {designPreview ? (
                <div className="relative group w-32 h-32 rounded-lg overflow-hidden border border-border bg-bg-subtle/30 shadow-sm transition hover:shadow-md">
                  <img
                    src={designPreview}
                    alt="Design Preview"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-fg-muted bg-bg-subtle/10 text-xs">
                  No image uploaded
                </div>
              )}
              {!artImageDisabled && (
                <Input
                  id="design_image"
                  type="file"
                  accept="image/*"
                  className="cursor-pointer file:text-fg file:font-semibold text-xs"
                  onChange={(e) => setData('design_image', e.target.files?.[0] || null)}
                />
              )}
            </div>
          </Field>
        </div>
      </div>

      {/* SECTION 3: Attributes */}
      <div className="rounded-xl border border-border bg-bg-elev p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border/60">
          <div className="p-2 rounded-lg bg-fg/10 text-fg">
            <Sliders size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-fg">Card Attributes</h3>
            <p className="text-sm text-fg-muted">Stats, transport settings, costs, speed parameters, and targets.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {/* Row 1: Core Gameplay Attributes */}
          <Field label="Rarity" htmlFor="rarity" error={errors.rarity}>
            <NativeSelect
              id="rarity"
              value={data.rarity}
              onChange={(v) => setData('rarity', v)}
              options={options.rarities}
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Type" htmlFor="card_type" error={errors.card_type}>
            <NativeSelect
              id="card_type"
              value={data.card_type}
              onChange={(v) => setData('card_type', v)}
              options={options.card_types}
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Cost (elixir)" htmlFor="cost" error={errors.cost}>
            <Input
              id="cost"
              type="number"
              min={0}
              value={data.cost}
              onChange={(e) => setData('cost', e.target.value)}
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Arena (stage)" htmlFor="arena" error={errors.arena}>
            <Input
              id="arena"
              type="number"
              min={0}
              value={data.arena}
              onChange={(e) => setData('arena', e.target.value)}
              disabled={metadataDisabled}
            />
          </Field>

          {/* Row 2: Deployment & Targeting Attributes */}
          <Field label="Count" htmlFor="count" error={errors.count}>
            <Input
              id="count"
              type="number"
              min={0}
              value={data.count}
              onChange={(e) => setData('count', e.target.value)}
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Focus" htmlFor="focus" error={errors.focus}>
            <NativeSelect
              id="focus"
              value={data.focus}
              onChange={(v) => setData('focus', v)}
              options={options.focuses}
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Form Size" htmlFor="form_size" error={errors.form_size}>
            <NativeSelect
              id="form_size"
              value={data.form_size}
              onChange={(v) => setData('form_size', v)}
              options={options.form_sizes}
              includeBlank
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Transport" htmlFor="transport" error={errors.transport}>
            <NativeSelect
              id="transport"
              value={data.transport}
              onChange={(v) => setData('transport', v)}
              options={options.transports}
              includeBlank
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Targets" error={errors.target_air || errors.target_ground}>
            <div className="flex h-9 items-center gap-4 bg-bg-subtle/20 px-3 border border-border rounded-md">
              <label className={cn("flex items-center gap-1.5 text-sm select-none", !metadataDisabled && "cursor-pointer")}>
                <input
                  type="checkbox"
                  checked={data.target_air}
                  onChange={(e) => setData('target_air', e.target.checked)}
                  className={cn("h-4 w-4 rounded border-border accent-fg", !metadataDisabled && "cursor-pointer")}
                  disabled={metadataDisabled}
                />
                Air
              </label>
              <label className={cn("flex items-center gap-1.5 text-sm select-none", !metadataDisabled && "cursor-pointer")}>
                <input
                  type="checkbox"
                  checked={data.target_ground}
                  onChange={(e) => setData('target_ground', e.target.checked)}
                  className={cn("h-4 w-4 rounded border-border accent-fg", !metadataDisabled && "cursor-pointer")}
                  disabled={metadataDisabled}
                />
                Ground
              </label>
            </div>
          </Field>

          {/* Row 3: Movement & Attack Attributes */}
          <Field label="Speed" htmlFor="speed" error={errors.speed}>
            <NativeSelect
              id="speed"
              value={data.speed}
              onChange={(v) => setData('speed', v)}
              options={options.speeds}
              includeBlank
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Range (tiles)" htmlFor="range_tiles" error={errors.range_tiles}>
            <Input
              id="range_tiles"
              value={data.range_tiles}
              onChange={(e) => setData('range_tiles', e.target.value)}
              placeholder="e.g. 5, Melee: Short"
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="Hit speed (s)" htmlFor="hit_speed" error={errors.hit_speed}>
            <Input
              id="hit_speed"
              value={data.hit_speed}
              onChange={(e) => setData('hit_speed', e.target.value)}
              placeholder="e.g. 1.2"
              disabled={metadataDisabled}
            />
          </Field>

          <Field label="First hit speed (s)" htmlFor="first_hit_speed" error={errors.first_hit_speed}>
            <Input
              id="first_hit_speed"
              value={data.first_hit_speed}
              onChange={(e) => setData('first_hit_speed', e.target.value)}
              placeholder="e.g. 0.5"
              disabled={metadataDisabled}
            />
          </Field>
        </div>
      </div>
    </div>
  )
}
